import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  AfterViewInit,
  OnDestroy,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ViewChild,
  ElementRef,
  HostListener,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatSortModule, MatSort } from '@angular/material/sort';
import { MatPaginatorModule, MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatRadioModule } from '@angular/material/radio';
import { MatDividerModule } from '@angular/material/divider';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { FormsModule } from '@angular/forms';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { BreakpointObserver } from '@angular/cdk/layout';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { Subject, BehaviorSubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

// Import core types and interfaces
import { NestedKeyOf } from '../../core/types/field-path.type';
import { ColDef } from '../../core/interfaces/col-def.interface';
import { GridConfig, DEFAULT_GRID_CONFIG } from '../../core/interfaces/grid-config.interface';
import { GridApi } from '../../core/interfaces/grid-api.interface';
import { SortChangedEvent, SortState } from '../../core/interfaces/sort-state.interface';
import { FilterChangedEvent, FilterState } from '../../core/interfaces/filter-state.interface';
import { PageChangedEvent, PaginationState } from '../../core/interfaces/pagination-state.interface';
import {
  GridReadyEvent,
  SelectionChangedEvent,
  RowClickedEvent,
  CellClickedEvent,
  RowExpansionChangedEvent,
} from '../../core/interfaces/grid-events.interface';
import {
  ContextMenuOpenedEvent,
  ContextMenuItemSelectedEvent,
} from '../../core/interfaces/context-menu.interface';

// Import utilities
import { getNestedValue } from '../../core/utils/field-accessor.util';
import { sortByMultipleFields } from '../../core/utils/sort.util';
import {
  filterBySearchTerm,
  filterByColumnFilters,
  getFilterOptionsWithCounts,
} from '../../core/utils/filter.util';

// Import column filter component
import { ColumnFilterComponent } from '../column-filter/column-filter.component';

// Import global search component
import { GlobalSearchComponent } from '../global-search/global-search.component';

// Import selection column component
import { SelectionColumnComponent } from '../selection-column/selection-column.component';

// Import column visibility component
import { ColumnVisibilityComponent, ColumnVisibilityOption } from '../column-visibility/column-visibility.component';

// Import expansion column component
import { ExpansionColumnComponent } from '../expansion-column/expansion-column.component';

// Import tree expand column component
import { TreeExpandColumnComponent } from '../tree-expand-column/tree-expand-column.component';

// Import cell renderer loader component
import { CellRendererLoaderComponent } from '../cell-renderer-loader/cell-renderer-loader.component';

// Import header renderer loader component
import { HeaderRendererLoaderComponent } from '../header-renderer-loader/header-renderer-loader.component';

// Import footer cell renderer loader component
import { FooterCellRendererLoaderComponent } from '../footer-cell-renderer-loader/footer-cell-renderer-loader.component';

// Import group row component
import { GroupRowComponent } from '../group-row/group-row.component';

// Import group panel component
import { GroupPanelComponent } from '../group-panel/group-panel.component';

// Import context menu component
import { ContextMenuComponent } from '../context-menu/context-menu.component';

// Import context menu service
import { ContextMenuService } from '../../core/services/context-menu.service';

// Import tree utilities
import {
  TreeNode,
  TreeConfig,
  flattenTree,
  getVisibleNodes,
  toggleNodeExpansion,
  expandNode,
  collapseNode,
  expandAll as expandAllTree,
  collapseAll as collapseAllTree,
  expandToNode,
  getMaxDepth,
  getTotalNodeCount,
  getVisibleNodeCount,
  getExpandedNodeCount,
} from '../../core/utils/tree.util';

// Import export service
import { GridExportService } from '../../core/services/grid-export.service';

// Import responsive utilities
import {
  Breakpoint,
  getCurrentBreakpoint,
  getResponsiveColumns,
  shouldUseTouchControls,
  getResponsiveClasses,
  DEFAULT_BREAKPOINTS,
} from '../../core/utils/responsive.util';

// Import grouping utilities
import {
  GroupNode,
  GroupExpansionState,
  groupDataByFields,
  flattenGroupedData,
  expandGroup,
  collapseGroup,
  toggleGroupExpansion,
  expandAllGroups,
  collapseAllGroups,
  isGroupExpanded,
  getTotalGroupCount,
  getExpandedGroupCount,
  getMaxGroupDepth,
  AggregateConfig,
  calculateGroupAggregates,
} from '../../core/utils/grouping.util';

/**
 * Smart Data Grid Component
 * A highly configurable, type-safe data grid component inspired by ag-grid
 *
 * @template T - The type of data displayed in the grid
 *
 * @example
 * ```typescript
 * <app-smart-grid
 *   [data]="orders"
 *   [columnDefs]="columnDefs"
 *   [config]="gridConfig"
 *   (sortChanged)="onSortChanged($event)"
 *   (rowClicked)="onRowClicked($event)">
 * </app-smart-grid>
 * ```
 */
@Component({
  selector: 'app-smart-grid',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    MatExpansionModule,
    MatRadioModule,
    MatDividerModule,
    MatCheckboxModule,
    FormsModule,
    ScrollingModule,
    ColumnFilterComponent,
    GlobalSearchComponent,
    SelectionColumnComponent,
    ColumnVisibilityComponent,
    ExpansionColumnComponent,
    TreeExpandColumnComponent,
    CellRendererLoaderComponent,
    HeaderRendererLoaderComponent,
    FooterCellRendererLoaderComponent,
    GroupRowComponent,
    GroupPanelComponent,
    ContextMenuComponent,
  ],
  templateUrl: './smart-grid.component.html',
  styleUrls: ['./smart-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0', display: 'none' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
})
export class SmartGridComponent<T> implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  // ============================================================================
  // INPUTS
  // ============================================================================

  /**
   * Data to display in the grid
   */
  private _data: T[] = [];

  @Input()
  set data(value: T[]) {
    this._data = value;

    // Update data source when data changes
    if (this.isInitialized) {
      // Clear filter options cache when data changes
      // This ensures fresh filter options are calculated for new data
      this.filterOptionsCache.clear();

      // Apply local filters to new data (important for mixed API/Local modes)
      // When pagination/sorting are API mode but filters are local,
      // new data from server should be filtered locally
      this.applyFilters();
      this.paginationState.totalItems = value.length;

      // Force change detection to refresh filter options
      // This ensures getFilterOptions() is re-evaluated with new data
      // Critical for mixed API/Local modes where filter dropdowns need updating
      this.cdr.detectChanges();
    }
  }

  get data(): T[] {
    return this._data;
  }

  /**
   * Column definitions
   */
  @Input() columnDefs: ColDef<T>[] = [];

  /**
   * Grid configuration
   */
  @Input() config: GridConfig<T> = {};

  /**
   * Loading state
   */
  @Input() loading: boolean = false;

  /**
   * Total number of records (for API mode pagination)
   * In API mode, this tells the paginator how many total records exist
   */
  @Input() totalRecords?: number;

  /**
   * Template for expandable row detail content
   */
  @Input() detailTemplate?: any;

  // ============================================================================
  // OUTPUTS (Events)
  // ============================================================================

  /**
   * Emitted when grid is ready and initialized
   */
  @Output() gridReady = new EventEmitter<GridReadyEvent<T>>();

  /**
   * Emitted when sort state changes (API mode)
   */
  @Output() sortChanged = new EventEmitter<SortChangedEvent<T>>();

  /**
   * Emitted when filter state changes (API mode)
   */
  @Output() filterChanged = new EventEmitter<FilterChangedEvent<T>>();

  /**
   * Emitted when page changes (API mode)
   */
  @Output() pageChanged = new EventEmitter<PageChangedEvent>();

  /**
   * Emitted when selection changes
   */
  @Output() selectionChanged = new EventEmitter<SelectionChangedEvent<T>>();

  /**
   * Emitted when a row is clicked
   */
  @Output() rowClicked = new EventEmitter<RowClickedEvent<T>>();

  /**
   * Emitted when a cell is clicked
   */
  @Output() cellClicked = new EventEmitter<CellClickedEvent<T>>();

  /**
   * Emitted when search term changes (API mode)
   */
  @Output() searchChanged = new EventEmitter<{ searchTerm: string; previousTerm: string }>();

  /**
   * Emitted when context menu is opened (right-click on row)
   */
  @Output() contextMenuOpened = new EventEmitter<ContextMenuOpenedEvent<T>>();

  /**
   * Emitted when a context menu item is selected
   */
  @Output() contextMenuItemSelected = new EventEmitter<ContextMenuItemSelectedEvent<T>>();

  /**
   * Emitted when row expansion state changes
   */
  @Output() rowExpansionChanged = new EventEmitter<RowExpansionChangedEvent<T>>();

  // ============================================================================
  // VIEW CHILDREN
  // ============================================================================

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild('gridWrapper', { read: ElementRef }) gridWrapper!: ElementRef;

  // ============================================================================
  // PUBLIC PROPERTIES
  // ============================================================================

  /**
   * Grid API for programmatic control
   * Access via: @ViewChild(SmartGridComponent) grid; grid.api.method()
   */
  public api!: GridApi<T>;

  /**
   * Material table data source
   */
  public dataSource = new MatTableDataSource<T>([]);

  /**
   * Displayed column IDs for mat-table
   */
  public displayedColumns: string[] = [];

  /**
   * Visible column definitions (after hiding)
   */
  public visibleColumnDefs: ColDef<T>[] = [];

  /**
   * Merged configuration (user config + defaults)
   * Initialized in mergeConfigurations()
   */
  public mergedConfig!: GridConfig<T>;

  // ============================================================================
  // PRIVATE PROPERTIES
  // ============================================================================

  private destroy$ = new Subject<void>();
  private isInitialized = false;

  // State
  private sortState: SortState<T>[] = [];
  private filterState: FilterState<T> = {};
  private paginationState: PaginationState = {
    pageIndex: 0,
    pageSize: 50,
    totalItems: 0,
  };
  private searchTerm: string = '';
  private selectedRows: Set<T> = new Set();
  private lastClickedRowIndex: number | null = null;
  private expandedRows: Set<T> = new Set();

  // Tree mode state
  private flattenedTreeData: TreeNode<T>[] = [];
  private isTreeMode: boolean = false;

  // Column visibility
  public columnVisibilityOptions: ColumnVisibilityOption[] = [];

  // Grid density (for settings panel)
  public gridDensity: 'compact' | 'comfortable' | 'spacious' = 'comfortable';

  // Settings panel state
  public isSettingsPanelOpen: boolean = false;
  public selectedDensity: 'compact' | 'comfortable' | 'spacious' = 'comfortable';

  // Responsive properties
  private currentBreakpoint: Breakpoint = 'desktop';
  private viewportWidth: number = 0;
  public responsiveClasses: string = '';

  // Grouping properties
  private groupByFields: NestedKeyOf<T>[] = [];
  private groupedData: GroupNode<T>[] = [];
  private groupExpansionState: GroupExpansionState = new Map();
  private isGroupingEnabledFlag: boolean = false;

  // Filter options cache (performance optimization)
  private filterOptionsCache: Map<string, Array<{ value: any; label: string; count: number }>> = new Map();

  // Footer data subjects cache (one BehaviorSubject per column for reactive data streaming)
  private footerDataSubjects = new Map<string, BehaviorSubject<T[]>>();

  // ============================================================================
  // CONSTRUCTOR
  // ============================================================================

  constructor(
    private cdr: ChangeDetectorRef,
    private exportService: GridExportService,
    private breakpointObserver: BreakpointObserver,
    private contextMenuService: ContextMenuService<T>
  ) {}

  // ============================================================================
  // LIFECYCLE HOOKS
  // ============================================================================

  ngOnInit(): void {
    this.initializeGrid();
    this.initializeResponsive();
    this.initializeGrouping();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Detectar cambios en totalRecords y marcar para change detection
    if (changes['totalRecords']) {
      this.cdr.markForCheck();
    }
  }

  ngAfterViewInit(): void {
    // Connect paginator after view initialization
    if (this.mergedConfig.pagination?.enabled && this.paginator) {
      // In API mode, listen to pagination events instead of connecting directly
      if (this.mergedConfig.pagination.mode === 'api') {
        this.setupServerSidePagination();
      } else {
        // Client-side pagination
        this.dataSource.paginator = this.paginator;
      }
    }

    // Connect sort after view initialization
    if (this.mergedConfig.sorting?.enabled && this.sort) {
      const isApiMode = this.mergedConfig.sorting?.mode === 'api';

      if (isApiMode) {
        // API mode: Solo escuchar eventos, NO ordenar localmente
        this.setupApiModeSorting();

        // Apply default sort visual state if configured
        if (this.mergedConfig.sorting?.defaultSort &&
            this.mergedConfig.sorting.defaultSort.length > 0) {
          const defaultSort = this.mergedConfig.sorting.defaultSort[0];

          // Set sort properties
          this.sort.active = defaultSort.field as string;
          this.sort.direction = defaultSort.direction as 'asc' | 'desc' | '';

          // Emit sortChange event to update Material's visual indicators
          this.sort.sortChange.emit({
            active: this.sort.active,
            direction: this.sort.direction
          });

          this.cdr.detectChanges();
        }
      } else {
        // Client mode: Ordenar localmente
        if (!this.mergedConfig.sorting.multiColumn) {
          // Single-column: usar MatSort integrado
          this.dataSource.sort = this.sort;
        } else {
          // Multi-column: usar lógica personalizada
          this.setupMultiColumnSort();
        }
      }
    }

    // FAB positioning now handled by CSS (no calculation needed)
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    // Complete and clean up footer data subjects
    this.footerDataSubjects.forEach(subject => subject.complete());
    this.footerDataSubjects.clear();
  }

  // ============================================================================
  // FAB POSITIONING
  // ============================================================================
  // NOTE: FAB now uses CSS absolute positioning (top: 120px, right: 24px)
  // No JavaScript calculation needed - simpler and more reliable!

  // ============================================================================
  // INITIALIZATION
  // ============================================================================

  private initializeGrid(): void {
    // Merge configurations
    this.mergeConfigurations();

    // Initialize column definitions
    this.initializeColumns();

    // Initialize data
    this.initializeData();

    // Create API
    this.createGridApi();

    // Emit grid ready event
    this.emitGridReady();

    this.isInitialized = true;
  }

  private mergeConfigurations(): void {
    // Deep merge configuration with defaults
    // Type assertion is safe here: DEFAULT_GRID_CONFIG is runtime-compatible
    // When user provides config values, they will have proper NestedKeyOf<T> types
    this.mergedConfig = {
      ...DEFAULT_GRID_CONFIG,
      ...this.config,
      // Deep merge nested objects to ensure proper override behavior
      sorting: { ...DEFAULT_GRID_CONFIG.sorting, ...this.config.sorting },
      pagination: { ...DEFAULT_GRID_CONFIG.pagination, ...this.config.pagination },
      search: { ...DEFAULT_GRID_CONFIG.search, ...this.config.search } ,
      selection: { ...DEFAULT_GRID_CONFIG.selection, ...this.config.selection },
      responsive: { ...DEFAULT_GRID_CONFIG.responsive, ...this.config.responsive },
      contextMenu: { ...DEFAULT_GRID_CONFIG.contextMenu, ...this.config.contextMenu },
      settingsPanel: { ...DEFAULT_GRID_CONFIG.settingsPanel, ...this.config.settingsPanel },
    } as GridConfig<T>;
  }

  private initializeColumns(): void {
    // Filter out hidden columns
    this.visibleColumnDefs = this.columnDefs.filter(col => !col.hide);

    // Apply responsive column filtering if enabled
    if (this.mergedConfig.responsive?.enabled) {
      this.applyResponsiveColumnFiltering();
    }

    // Initialize column visibility options
    this.initializeColumnVisibilityOptions();

    // Generate displayed columns array for mat-table
    this.updateDisplayedColumns();
  }

  /**
   * Initialize column visibility options for the visibility toggle
   */
  private initializeColumnVisibilityOptions(): void {
    this.columnVisibilityOptions = this.columnDefs
      .filter(col => col.field) // Only columns with fields
      .map(col => ({
        field: col.field as string,
        headerName: col.headerName || this.humanize(col.field as string),
        visible: !col.hide,
        disabled: false, // Can be extended to mark some columns as mandatory
      }));
  }

  /**
   * Update displayed columns based on visibility state
   */
  private updateDisplayedColumns(): void {
    // Filter visible columns based on visibility options
    this.visibleColumnDefs = this.columnDefs.filter(col => {
      if (!col.field) return !col.hide; // Non-field columns use hide property

      const visibilityOption = this.columnVisibilityOptions.find(
        opt => opt.field === col.field
      );
      return visibilityOption ? visibilityOption.visible : !col.hide;
    });

    // Generate displayed columns array for mat-table
    let columns = this.visibleColumnDefs.map((col, index) =>
      col.colId || col.field as string || `col-${index}`
    );

    // Add tree expand column if tree mode is enabled (leftmost position)
    if (this.isTreeMode && this.mergedConfig.treeData?.showExpandControls !== false) {
      columns = ['treeExpand', ...columns];
    }

    // Add expansion column if enabled (after tree expand)
    if (this.mergedConfig.rowExpansion?.enabled) {
      if (this.isTreeMode) {
        // Tree mode: insert expansion after treeExpand column
        columns = [columns[0], 'expansion', ...columns.slice(1)];
      } else {
        // Normal mode: insert expansion at the beginning
        columns = ['expansion', ...columns];
      }
    }

    // Add selection column if enabled (after expansion/tree columns)
    if (this.mergedConfig.selection?.enabled && this.mergedConfig.selection?.showCheckboxColumn) {
      let insertIndex = 0;
      if (this.isTreeMode && this.mergedConfig.treeData?.showExpandControls !== false) insertIndex++;
      if (this.mergedConfig.rowExpansion?.enabled) insertIndex++;
      columns.splice(insertIndex, 0, 'selection');
    }

    this.displayedColumns = columns;
  }

  private initializeData(): void {
    // Check if tree mode is enabled
    this.isTreeMode = this.mergedConfig.treeData?.enabled === true;

    if (this.isTreeMode) {
      // Tree mode: flatten data
      this.initializeTreeData();
    } else {
      // Normal mode: use data as-is
      this.dataSource.data = this.data;
      this.paginationState.totalItems = this.data.length;
    }
  }

  /**
   * Initialize tree data
   * Flattens hierarchical data and sets up visible nodes
   */
  private initializeTreeData(): void {
    if (!this.mergedConfig.treeData) return;

    const config: TreeConfig<T> = {
      childrenField: this.mergedConfig.treeData.childrenField || ('children' as keyof T),
      idField: this.mergedConfig.rowIdField || ('_id' as keyof T),
      expandDepth: this.mergedConfig.treeData.expandDepth ?? 0,
    };

    // Flatten tree structure
    this.flattenedTreeData = flattenTree(this.data, config);

    // Get initially visible nodes (based on expand depth)
    this.updateTreeDataSource();
  }

  /**
   * Update data source with visible tree nodes
   */
  private updateTreeDataSource(): void {
    const visibleNodes = getVisibleNodes(this.flattenedTreeData);

    // Extract data from tree nodes
    const visibleData = visibleNodes.map(node => node.data);

    this.dataSource.data = visibleData;
    this.paginationState.totalItems = visibleData.length;
  }

  private createGridApi(): void {
    // Grid API with full functionality
    // Note: Some methods are stubs (TODO) and will be implemented in future phases
    this.api = {
      // ============================================================================
      // DATA OPERATIONS (Implemented)
      // ============================================================================
      setData: (newData: T[]) => {
        this.data = newData;
        this.dataSource.data = newData;
        this.paginationState.totalItems = newData.length;
        this.cdr.markForCheck();
      },
      getData: () => this.data,
      getDisplayedData: () => this.dataSource.data,
      refreshData: () => {
        this.dataSource.data = [...this.data];
        this.cdr.markForCheck();
      },
      addRows: (rows: T[], index?: number) => {
        // TODO: Implement in future phase
        console.warn('GridApi.addRows: Not yet implemented');
      },
      updateRows: (rows: T[]) => {
        // TODO: Implement in future phase
        console.warn('GridApi.updateRows: Not yet implemented');
      },
      removeRows: (rows: T[]) => {
        // TODO: Implement in future phase
        console.warn('GridApi.removeRows: Not yet implemented');
      },
      getRowById: (id: any) => {
        const rowIdField = this.mergedConfig.rowIdField || '_id';
        return this.data.find(row => (row as any)[rowIdField] === id);
      },
      getRowByIndex: (index: number) => {
        return this.dataSource.data[index];
      },

      // ============================================================================
      // SORTING (Partially Implemented)
      // ============================================================================
      setSortState: (sortStates: SortState<T>[]) => {
        // TODO: Implement in future phase
        console.warn('GridApi.setSortState: Not yet implemented');
      },
      getSortState: () => this.sortState,
      clearSort: () => this.clearAllSorts(),
      sortByField: (field: NestedKeyOf<T>, direction: 'asc' | 'desc', addToExisting?: boolean) => {
        // TODO: Implement in future phase
        console.warn('GridApi.sortByField: Not yet implemented');
      },

      // ============================================================================
      // FILTERING (Partially Implemented)
      // ============================================================================
      setFilterState: (filterState: FilterState<T>) => {
        this.filterState = filterState;
        this.applyFilters();
        this.cdr.markForCheck();
      },
      getFilterState: () => this.filterState,
      clearFilters: () => this.clearAllFilters(),
      setColumnFilter: (field: NestedKeyOf<T>, value: any) => {
        // TODO: Implement in future phase
        console.warn('GridApi.setColumnFilter: Not yet implemented');
      },
      clearColumnFilter: (field: NestedKeyOf<T>) => {
        // TODO: Implement in future phase
        console.warn('GridApi.clearColumnFilter: Not yet implemented');
      },

      // ============================================================================
      // SEARCH (Partially Implemented)
      // ============================================================================
      setSearchTerm: (term: string) => {
        this.searchTerm = term;
        this.applyFilters();
        this.cdr.markForCheck();
      },
      getSearchTerm: () => this.searchTerm,
      clearSearch: () => {
        this.searchTerm = '';
        this.applyFilters();
        this.cdr.markForCheck();
      },

      // ============================================================================
      // PAGINATION (Stub)
      // ============================================================================
      setPaginationState: (state: Partial<PaginationState>) => {
        // TODO: Implement in future phase
        console.warn('GridApi.setPaginationState: Not yet implemented');
      },
      getPaginationState: () => this.paginationState,
      goToPage: (pageIndex: number) => {
        // TODO: Implement in future phase
        console.warn('GridApi.goToPage: Not yet implemented');
      },
      nextPage: () => {
        // TODO: Implement in future phase
        console.warn('GridApi.nextPage: Not yet implemented');
      },
      previousPage: () => {
        // TODO: Implement in future phase
        console.warn('GridApi.previousPage: Not yet implemented');
      },
      firstPage: () => {
        // TODO: Implement in future phase
        console.warn('GridApi.firstPage: Not yet implemented');
      },
      lastPage: () => {
        // TODO: Implement in future phase
        console.warn('GridApi.lastPage: Not yet implemented');
      },
      setPageSize: (pageSize: number) => {
        // TODO: Implement in future phase
        console.warn('GridApi.setPageSize: Not yet implemented');
      },

      // ============================================================================
      // SELECTION (Implemented)
      // ============================================================================
      selectRows: (rows: T[], addToExisting?: boolean) => {
        // TODO: Implement in future phase
        console.warn('GridApi.selectRows: Not yet implemented');
      },
      deselectRows: (rows: T[]) => {
        // TODO: Implement in future phase
        console.warn('GridApi.deselectRows: Not yet implemented');
      },
      selectAll: () => this.selectAll(),
      deselectAll: () => this.deselectAll(),
      getSelectedRows: () => this.getSelectedRows(),
      getSelectedRowIds: () => {
        const rowIdField = this.mergedConfig.rowIdField || '_id';
        return this.getSelectedRows().map(row => (row as any)[rowIdField]);
      },
      isRowSelected: (row: T) => this.isRowSelected(row),
      toggleRowSelection: (row: T) => {
        const rowIndex = this.dataSource.data.indexOf(row);
        this.toggleRowSelection(row, rowIndex);
      },

      // ============================================================================
      // ROW EXPANSION (Implemented)
      // ============================================================================
      expandRows: (rows: T | T[]) => {
        const rowArray = Array.isArray(rows) ? rows : [rows];

        // If multipleExpand is false, clear existing expansions
        if (!this.mergedConfig.rowExpansion?.multipleExpand) {
          this.expandedRows.clear();
        }

        rowArray.forEach(row => this.expandedRows.add(row));
        this.cdr.markForCheck();
      },
      collapseRows: (rows: T | T[]) => {
        const rowArray = Array.isArray(rows) ? rows : [rows];
        rowArray.forEach(row => this.expandedRows.delete(row));
        this.cdr.markForCheck();
      },
      toggleRowExpansion: (row: T) => {
        this.toggleRowExpansion(row);
      },
      expandAll: () => {
        this.dataSource.data.forEach(row => this.expandedRows.add(row));
        this.cdr.markForCheck();
      },
      collapseAll: () => {
        this.expandedRows.clear();
        this.cdr.markForCheck();
      },
      getExpandedRows: () => {
        return Array.from(this.expandedRows);
      },
      isRowExpanded: (row: T) => {
        return this.isRowExpanded(row);
      },

      // ============================================================================
      // COLUMNS (Partially Implemented)
      // ============================================================================
      setColumnVisible: (field: NestedKeyOf<T>, visible: boolean) => {
        // TODO: Implement in future phase
        console.warn('GridApi.setColumnVisible: Not yet implemented');
      },
      getVisibleColumns: () => {
        return this.visibleColumnDefs.map(col => col.field).filter((f): f is NestedKeyOf<T> => Boolean(f));
      },
      getHiddenColumns: () => {
        const visible = new Set(this.visibleColumnDefs.map(col => col.field).filter(Boolean));
        return this.columnDefs
          .map(col => col.field)
          .filter((field): field is NestedKeyOf<T> => field !== undefined && !visible.has(field));
      },
      hideColumns: (fields: NestedKeyOf<T>[]) => {
        // TODO: Implement in future phase
        console.warn('GridApi.hideColumns: Not yet implemented');
      },
      showColumns: (fields: NestedKeyOf<T>[]) => {
        // TODO: Implement in future phase
        console.warn('GridApi.showColumns: Not yet implemented');
      },
      showAllColumns: () => {
        // TODO: Implement in future phase
        console.warn('GridApi.showAllColumns: Not yet implemented');
      },
      pinColumn: (field: NestedKeyOf<T>, position: 'left' | 'right' | null) => {
        // TODO: Implement in future phase
        console.warn('GridApi.pinColumn: Not yet implemented');
      },
      getPinnedColumns: () => {
        // TODO: Implement in future phase
        console.warn('GridApi.getPinnedColumns: Not yet implemented');
        return { left: [], right: [] };
      },
      autoSizeColumns: (fields?: NestedKeyOf<T>[]) => this.autoSizeColumns(fields as string[]),
      autoSizeAllColumns: () => this.autoSizeAllColumns(),
      sizeColumnsToFit: () => this.sizeColumnsToFit(),

      // ============================================================================
      // STATE (Stub - Future Phase)
      // ============================================================================
      getGridState: () => {
        console.warn('GridApi.getGridState: Not yet implemented');
        return {};
      },
      setGridState: (state: any) => {
        console.warn('GridApi.setGridState: Not yet implemented');
      },
      saveState: () => {
        console.warn('GridApi.saveState: Not yet implemented');
      },
      loadState: () => {
        console.warn('GridApi.loadState: Not yet implemented');
      },
      clearState: () => {
        console.warn('GridApi.clearState: Not yet implemented');
      },
      resetGrid: () => {
        this.clearAllFilters();
        this.clearAllSorts();
        this.deselectAll();
        this.searchTerm = '';
        this.applyFilters();
        this.cdr.markForCheck();
      },

      // ============================================================================
      // GROUPING & TREE (Implemented)
      // ============================================================================
      setGroupBy: (fields: NestedKeyOf<T>[]) => this.setGroupBy(fields),
      getGroupBy: () => this.getGroupBy(),
      clearGrouping: () => this.clearGrouping(),
      expandTreeNode: (node: T, expandChildren?: boolean) => {
        this.expandTreeNode(node, expandChildren);
      },
      collapseTreeNode: (node: T, collapseChildren?: boolean) => {
        this.collapseTreeNode(node, collapseChildren);
      },

      // ============================================================================
      // EXPORT (Implemented)
      // ============================================================================
      exportToCsv: (filename?: string, options?: any) => {
        const exportOptions = {
          ...options,
          filename: filename || 'export',
          dataScope: options?.dataScope || 'displayed',
        };
        const dataToExport = exportOptions.dataScope === 'all' ? this.data : this.dataSource.data;
        this.exportService.exportToCsv(dataToExport, this.visibleColumnDefs, exportOptions);
      },
      exportToExcel: (filename?: string, options?: any) => {
        const exportOptions = {
          ...options,
          filename: filename || 'export',
          dataScope: options?.dataScope || 'displayed',
        };
        const dataToExport = exportOptions.dataScope === 'all' ? this.data : this.dataSource.data;
        this.exportService.exportToExcel(dataToExport, this.visibleColumnDefs, exportOptions);
      },
      exportDisplayedData: (format: 'csv' | 'excel', filename?: string) => {
        const fname = filename || 'export';
        if (format === 'csv') {
          this.exportService.exportToCsv(this.dataSource.data, this.visibleColumnDefs, { filename: fname });
        } else {
          this.exportService.exportToExcel(this.dataSource.data, this.visibleColumnDefs, { filename: fname });
        }
      },

      // ============================================================================
      // UTILITIES (Partially Implemented)
      // ============================================================================
      redraw: () => {
        this.cdr.markForCheck();
      },
      showLoading: () => {
        this.loading = true;
        this.cdr.markForCheck();
      },
      hideLoading: () => {
        this.loading = false;
        this.cdr.markForCheck();
      },
      getRowCount: () => this.data.length,
      getDisplayedRowCount: () => this.dataSource.data.length,
      scrollToRow: (row: T) => {
        console.warn('GridApi.scrollToRow: Not yet implemented');
      },
      scrollToTop: () => {
        console.warn('GridApi.scrollToTop: Not yet implemented');
      },
      scrollToBottom: () => {
        console.warn('GridApi.scrollToBottom: Not yet implemented');
      },
      getCellValue: (row: T, field: NestedKeyOf<T>) => {
        return getNestedValue(row, field as string);
      },
      destroy: () => {
        this.ngOnDestroy();
      },
    };
  }

  private emitGridReady(): void {
    setTimeout(() => {
      this.gridReady.emit({
        api: this.api,
        rowCount: this.data.length,
        columnCount: this.columnDefs.length,
      });
    }, 0);
  }

  // ============================================================================
  // RESPONSIVE LOGIC
  // ============================================================================

  /**
   * Initialize responsive functionality
   * Sets up viewport observer and applies initial responsive state
   */
  private initializeResponsive(): void {
    if (!this.mergedConfig.responsive?.enabled) {
      return;
    }

    // Get initial viewport width
    this.viewportWidth = window.innerWidth;

    // Get initial breakpoint
    const breakpoints = {
      mobile: this.mergedConfig.responsive.breakpoints?.mobile || DEFAULT_BREAKPOINTS.mobile,
      tablet: this.mergedConfig.responsive.breakpoints?.tablet || DEFAULT_BREAKPOINTS.tablet,
    };

    this.currentBreakpoint = getCurrentBreakpoint(this.viewportWidth, breakpoints);

    // Update responsive classes
    this.updateResponsiveClasses();

    // Observe viewport changes using CDK BreakpointObserver
    const mobileQuery = `(max-width: ${breakpoints.mobile}px)`;
    const tabletQuery = `(min-width: ${breakpoints.mobile + 1}px) and (max-width: ${breakpoints.tablet}px)`;
    const desktopQuery = `(min-width: ${breakpoints.tablet + 1}px)`;

    this.breakpointObserver
      .observe([mobileQuery, tabletQuery, desktopQuery])
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.viewportWidth = window.innerWidth;
        const newBreakpoint = getCurrentBreakpoint(this.viewportWidth, breakpoints);

        if (newBreakpoint !== this.currentBreakpoint) {
          this.currentBreakpoint = newBreakpoint;
          this.onBreakpointChange();
        }
      });
  }

  /**
   * Handle breakpoint change
   * Applies responsive column hiding and updates classes
   */
  private onBreakpointChange(): void {
    if (!this.mergedConfig.responsive?.enabled) {
      return;
    }

    // Apply responsive column filtering
    this.applyResponsiveColumnFiltering();

    // Update displayed columns
    this.updateDisplayedColumns();

    // Update responsive classes
    this.updateResponsiveClasses();

    // Mark for check
    this.cdr.markForCheck();
  }

  /**
   * Apply responsive column filtering based on current breakpoint
   */
  private applyResponsiveColumnFiltering(): void {
    if (!this.mergedConfig.responsive?.enabled || !this.mergedConfig.responsive?.autoHideColumns) {
      return;
    }

    const hideOnMobile = this.mergedConfig.responsive.hideColumnsOnMobile || [];
    const hideOnTablet = this.mergedConfig.responsive.hideColumnsOnTablet || [];
    const criticalColumns = this.mergedConfig.responsive.criticalColumns || [];

    // Start with all non-hidden columns
    let filteredColumns = this.columnDefs.filter(col => !col.hide);

    // Apply responsive hiding based on breakpoint
    if (this.currentBreakpoint === 'mobile') {
      filteredColumns = filteredColumns.filter(col => {
        // Always show critical columns
        if (col.field && criticalColumns.includes(col.field as any)) {
          return true;
        }
        // Hide columns in hideOnMobile list
        return !col.field || !hideOnMobile.includes(col.field as any);
      });
    } else if (this.currentBreakpoint === 'tablet') {
      filteredColumns = filteredColumns.filter(col => {
        // Always show critical columns
        if (col.field && criticalColumns.includes(col.field as any)) {
          return true;
        }
        // Hide columns in hideOnTablet list
        return !col.field || !hideOnTablet.includes(col.field as any);
      });
    }

    this.visibleColumnDefs = filteredColumns;
  }

  /**
   * Update responsive CSS classes
   */
  private updateResponsiveClasses(): void {
    if (!this.mergedConfig.responsive?.enabled) {
      this.responsiveClasses = '';
      return;
    }

    const compactMode = this.currentBreakpoint === 'tablet' && this.mergedConfig.responsive?.compactModeOnTablet;
    this.responsiveClasses = getResponsiveClasses(this.currentBreakpoint, compactMode);
  }

  /**
   * Check if touch controls should be used (public for template)
   */
  public shouldUseTouchMode(): boolean {
    if (!this.mergedConfig.responsive?.enabled || !this.mergedConfig.responsive?.touchOptimized) {
      return false;
    }
    return shouldUseTouchControls(this.currentBreakpoint);
  }

  /**
   * Get current breakpoint (public for template/debugging)
   */
  public getCurrentBreakpoint(): Breakpoint {
    return this.currentBreakpoint;
  }

  // ============================================================================
  // GROUPING LOGIC
  // ============================================================================

  /**
   * Initialize grouping functionality
   */
  private initializeGrouping(): void {
    if (!this.mergedConfig.grouping?.enabled) {
      this.isGroupingEnabledFlag = false;
      return;
    }

    this.isGroupingEnabledFlag = true;

    // Set default group by fields if specified
    if (this.mergedConfig.grouping.defaultGroupBy && this.mergedConfig.grouping.defaultGroupBy.length > 0) {
      this.groupByFields = this.mergedConfig.grouping.defaultGroupBy;
      this.applyGrouping();
    }
  }

  /**
   * Apply grouping to data
   */
  private applyGrouping(): void {
    if (!this.isGroupingEnabledFlag || this.groupByFields.length === 0) {
      // No grouping, use normal data
      this.groupedData = [];
      this.dataSource.data = this.data;
      return;
    }

    // Group the data (convert NestedKeyOf to string for utility function)
    const fieldStrings = this.groupByFields.map(f => f as string);
    this.groupedData = groupDataByFields(this.data, fieldStrings);

    // Flatten for display based on expansion state
    const flattenedData = flattenGroupedData(this.groupedData, this.groupExpansionState);

    // Update data source with flattened data (mix of groups and data nodes)
    this.dataSource.data = flattenedData as any[];
    this.cdr.markForCheck();
  }

  /**
   * Set group by fields
   */
  public setGroupBy(fields: NestedKeyOf<T>[]): void {
    this.groupByFields = fields;
    this.applyGrouping();
  }

  /**
   * Get current group by fields
   */
  public getGroupBy(): NestedKeyOf<T>[] {
    return [...this.groupByFields];
  }

  /**
   * Clear grouping
   */
  public clearGrouping(): void {
    this.groupByFields = [];
    this.groupedData = [];
    this.groupExpansionState.clear();
    this.applyGrouping();
  }

  /**
   * Toggle group expansion
   */
  public toggleGroupExpansion(groupKey: string): void {
    toggleGroupExpansion(groupKey, this.groupExpansionState);
    this.applyGrouping();
  }

  /**
   * Expand all groups
   */
  public expandAllGroups(): void {
    expandAllGroups(this.groupedData, this.groupExpansionState);
    this.applyGrouping();
  }

  /**
   * Collapse all groups
   */
  public collapseAllGroups(): void {
    collapseAllGroups(this.groupedData, this.groupExpansionState);
    this.applyGrouping();
  }

  /**
   * Check if a row is a group node (for template)
   */
  public isGroupNode(row: any): boolean {
    return row && row.type === 'group';
  }

  /**
   * Get group node (for template)
   */
  public getGroupNode(row: any): GroupNode<T> | null {
    if (this.isGroupNode(row)) {
      return row as GroupNode<T>;
    }
    return null;
  }

  /**
   * Get data from group node (for template)
   */
  public getDataFromNode(row: any): T | null {
    if (row && row.type === 'data') {
      return (row as GroupNode<T>).data || null;
    }
    return row; // Assume it's already data
  }

  /**
   * Check if grouping is enabled (for template)
   */
  public isGroupingEnabled(): boolean {
    return this.isGroupingEnabledFlag && this.groupByFields.length > 0;
  }

  /**
   * Handle group by changed event from group panel
   */
  public onGroupByChanged(fields: NestedKeyOf<T>[]): void {
    this.setGroupBy(fields);
  }

  // ============================================================================
  // PAGINATION LOGIC
  // ============================================================================

  /**
   * Setup server-side pagination (API mode)
   */
  private setupServerSidePagination(): void {
    if (!this.paginator) return;

    // Listen to pagination events
    this.paginator.page
      .pipe(takeUntil(this.destroy$))
      .subscribe((event: PageEvent) => {
        this.handlePageChange(event);
      });
  }

  /**
   * Handle page change event
   */
  private handlePageChange(event: PageEvent): void {
    // Update pagination state
    this.paginationState = {
      pageIndex: event.pageIndex,
      pageSize: event.pageSize,
      totalItems: event.length,
    };

    // Emit page changed event for API mode
    this.pageChanged.emit({
      pageIndex: event.pageIndex,
      pageSize: event.pageSize,
      previousPageIndex: event.previousPageIndex || 0,
      isInitial: false,
    });

    this.cdr.markForCheck();
  }

  // ============================================================================
  // SORTING LOGIC
  // ============================================================================

  /**
   * Setup API mode sorting (server-side)
   * Listens to sort events WITHOUT sorting locally
   */
  private setupApiModeSorting(): void {
    if (!this.sort) return;

    // Listen to sort changes
    this.sort.sortChange
      .pipe(takeUntil(this.destroy$))
      .subscribe((sort: any) => {
        this.handleApiSort(sort);
      });
  }

  /**
   * Handle API mode sort change
   * Updates sort state and emits event WITHOUT sorting locally
   */
  private handleApiSort(sort: any): void {
    const { active, direction } = sort;

    if (!direction) {
      // Sin ordenamiento
      this.sortState = [];
    } else {
      // Actualizar sort state
      if (this.mergedConfig.sorting?.multiColumn) {
        // Multi-column: agregar/actualizar en array
        const existingIndex = this.sortState.findIndex(s => s.field === active);

        if (existingIndex >= 0) {
          // Actualizar existente
          this.sortState[existingIndex] = {
            field: active as any,
            direction: direction as 'asc' | 'desc',
          };
        } else {
          // Agregar nuevo
          const maxColumns = this.mergedConfig.sorting?.maxMultiSortColumns || 3;

          if (this.sortState.length >= maxColumns) {
            // Remover el más antiguo
            this.sortState.shift();
          }

          this.sortState.push({
            field: active as any,
            direction: direction as 'asc' | 'desc',
          });
        }
      } else {
        // Single-column: reemplazar
        this.sortState = [{
          field: active as any,
          direction: direction as 'asc' | 'desc',
        }];
      }
    }

    // Emitir evento para que el padre maneje el sorting
    this.sortChanged.emit({
      sortStates: [...this.sortState],
      isInitial: false,
    });

    this.cdr.markForCheck();
  }

  /**
   * Setup multi-column sorting
   */
  private setupMultiColumnSort(): void {
    if (!this.sort) return;

    // Listen to sort changes
    this.sort.sortChange
      .pipe(takeUntil(this.destroy$))
      .subscribe((sort: any) => {
        this.handleMultiColumnSort(sort);
      });

    // Set custom sort accessor for nested fields
    this.dataSource.sortData = (data: T[], sort: MatSort) => {
      return this.sortDataMultiColumn(data);
    };
  }

  /**
   * Handle multi-column sort change
   */
  private handleMultiColumnSort(sort: any): void {
    const { active, direction } = sort;

    if (!direction) {
      // Remove this field from sort state
      this.sortState = this.sortState.filter(s => s.field !== active);
    } else {
      // Find if this field is already in sort state
      const existingIndex = this.sortState.findIndex(s => s.field === active);

      if (existingIndex >= 0) {
        // Update existing sort
        this.sortState[existingIndex] = {
          field: active as any,
          direction: direction as 'asc' | 'desc',
        };
      } else {
        // Add new sort
        const maxColumns = this.mergedConfig.sorting?.maxMultiSortColumns || 3;

        if (this.sortState.length >= maxColumns) {
          // Remove oldest sort
          this.sortState.shift();
        }

        this.sortState.push({
          field: active as any,
          direction: direction as 'asc' | 'desc',
        });
      }
    }

    // Preserve expansion state during sorting
    // Save which rows are currently expanded before sorting
    const expandedRowsBeforeSort = new Set(this.expandedRows);

    // Solo ordenar localmente si NO es modo API
    if (this.mergedConfig.sorting?.mode !== 'api') {
      // Client mode: ordenar localmente
      this.dataSource.data = this.sortDataMultiColumn(this.dataSource.data);

      // Restore expansion state after sorting
      // Clear any unintended expansions and restore only the previously expanded rows
      this.expandedRows.clear();
      expandedRowsBeforeSort.forEach(row => {
        // Verify the row still exists in the sorted data before restoring
        if (this.dataSource.data.includes(row)) {
          this.expandedRows.add(row);
        }
      });
    }

    // Emitir evento (para API mode o local mode con listeners)
    this.sortChanged.emit({
      sortStates: [...this.sortState],
      isInitial: false,
    });

    this.cdr.markForCheck();
  }

  /**
   * Sort data by multiple columns
   */
  private sortDataMultiColumn(data: T[]): T[] {
    if (!this.sortState.length) {
      return data;
    }

    return sortByMultipleFields(data, this.sortState);
  }

  // ============================================================================
  // PUBLIC METHODS (for template)
  // ============================================================================

  /**
   * Get header name for a column
   */
  public getHeaderName(colDef: ColDef<T>): string {
    if (colDef.headerName) {
      return colDef.headerName;
    }

    // Auto-generate from fields
    if (colDef.fields && colDef.fields.length > 1) {
      return colDef.fields.map(f => this.humanize(String(f))).join(' - ');
    }

    return this.humanize(colDef.field ? String(colDef.field) : '');
  }

  /**
   * Get field value from row data
   * @param row - The row data object
   * @param field - Type-safe nested field path
   */
  public getFieldValue(row: T, field: NestedKeyOf<T>): any {
    return getNestedValue(row, field as string);
  }

  /**
   * Build cell renderer params for a cell
   */
  public getCellRendererParams(row: T, colDef: ColDef<T>, rowIndex: number, colIndex: number): any {
    // Support both valueGetter (for computed columns) and field (for direct access)
    let value: any;
    if (colDef.valueGetter) {
      value = colDef.valueGetter(row);
    } else if (colDef.field) {
      value = getNestedValue(row, String(colDef.field));
    } else {
      value = null;
    }

    return {
      data: row,
      value: value,
      field: colDef.field,
      colDef: colDef,
      rowIndex: rowIndex,
      columnIndex: colIndex,
      api: this.api,
      ...colDef.cellRendererParams, // Spread any additional params from colDef
    };
  }

  /**
   * Get formatted field value (applies valueFormatter if defined)
   */
  public getFormattedFieldValue(row: T, colDef: ColDef<T>): any {
    if (!colDef.field) return null;

    const rawValue = getNestedValue(row, String(colDef.field));

    // Apply valueFormatter if defined
    if (colDef.valueFormatter) {
      return colDef.valueFormatter(rawValue, row);
    }

    return rawValue;
  }

  /**
   * Get column ID
   */
  public getColumnId(colDef: ColDef<T>, index: number): string {
    return colDef.colId || colDef.field as string || `col-${index}`;
  }

  /**
   * Get sort header value for mat-sort-header directive
   * Returns field as string or undefined if not sortable
   */
  public getSortHeader(colDef: ColDef<T>): string | undefined {
    if (colDef.sortable === false || !colDef.field) {
      return undefined;
    }
    return colDef.field as string;
  }

  /**
   * Get container styles for custom padding and font-size
   * Applies CSS variables that override default styles
   * Returns a style object that can be used with [ngStyle]
   */
  public getContainerStyles(): { [key: string]: string } {
    const styles: { [key: string]: string } = {};

    // Apply custom font-size (default: 12px from config)
    if (this.mergedConfig.styling?.fontSize) {
      styles['--grid-font-size'] = this.mergedConfig.styling.fontSize;
    }

    // Apply custom horizontal padding (default: 16px from config)
    if (this.mergedConfig.styling?.cellPaddingX) {
      styles['--grid-padding-x'] = this.mergedConfig.styling.cellPaddingX;
    }

    // Apply custom vertical padding (default: 12px from config)
    if (this.mergedConfig.styling?.cellPaddingY) {
      styles['--grid-padding-y'] = this.mergedConfig.styling.cellPaddingY;
    }

    return styles;
  }

  /**
   * Get column styles (width, minWidth, maxWidth) for applying to table cells
   * Returns a style object that can be used with [ngStyle]
   */
  public getColumnStyles(colDef: ColDef<T>): { [key: string]: string } {
    const styles: { [key: string]: string } = {};

    if (colDef.width) {
      styles['width'] = typeof colDef.width === 'number' ? `${colDef.width}px` : colDef.width;
    } else if (colDef.minWidth) {
      // CRITICAL: If no width but minWidth exists, use minWidth as width
      // This ensures table-layout: auto respects minWidth even without data
      styles['width'] = typeof colDef.minWidth === 'number' ? `${colDef.minWidth}px` : colDef.minWidth;
    }

    if (colDef.minWidth) {
      styles['min-width'] = typeof colDef.minWidth === 'number' ? `${colDef.minWidth}px` : colDef.minWidth;
    }

    if (colDef.maxWidth) {
      styles['max-width'] = typeof colDef.maxWidth === 'number' ? `${colDef.maxWidth}px` : colDef.maxWidth;
    }

    return styles;
  }

  /**
   * Get sort priority index for multi-column sorting
   * Returns the position (1-based) in the sort state, or undefined if not sorted
   */
  public getSortPriority(colDef: ColDef<T>): number | undefined {
    if (!this.mergedConfig.sorting?.multiColumn || !colDef.field) {
      return undefined;
    }

    const index = this.sortState.findIndex(s => s.field === colDef.field);
    return index >= 0 ? index + 1 : undefined;
  }

  // ============================================================================
  // FILTERING LOGIC
  // ============================================================================

  /**
   * Check if column is filterable
   */
  public isFilterableColumn(colDef: ColDef<T>): boolean {
    // Filter must be enabled globally
    if (!this.mergedConfig.columnFilters?.enabled) {
      return false;
    }

    // Column must have filterable set to true (or undefined)
    if (colDef.filterable === false) {
      return false;
    }

    // Column must have a field
    return !!colDef.field;
  }

  /**
   * Get filter options for a column (with caching for performance)
   * Cache key includes field, data length, and filter state to invalidate when these change
   */
  public getFilterOptions(colDef: ColDef<T>): Array<{ value: any; label: string; count: number }> {
    if (!colDef.field) {
      return [];
    }

    // Create cache key based on field, data, and filter state
    // This ensures cache is invalidated when any of these change
    const cacheKey = `${colDef.field}_${this.data.length}_${JSON.stringify(this.filterState)}`;

    // Return cached options if available
    if (this.filterOptionsCache.has(cacheKey)) {
      return this.filterOptionsCache.get(cacheKey)!;
    }

    // Get filter configuration
    const displayNameGetter = colDef.filterOptions?.displayNameGetter;
    const valueExtractor = colDef.filterOptions?.valueExtractor;

    // Calculate options and store in cache
    // IMPORTANT: Pass columnDefs so valueExtractor is used consistently
    const options = getFilterOptionsWithCounts(
      this.data,
      colDef.field as string,
      this.filterState,
      displayNameGetter,
      valueExtractor,
      this.columnDefs  // Pass columnDefs for valueExtractor support
    );

    this.filterOptionsCache.set(cacheKey, options);

    return options;
  }

  /**
   * Get selected filter values for a column
   */
  public getSelectedFilterValues(colDef: ColDef<T>): any[] {
    if (!colDef.field) {
      return [];
    }

    const fieldKey = colDef.field as string;
    const values = this.filterState[fieldKey];

    if (!values) {
      return [];
    }

    return Array.isArray(values) ? values : [values];
  }

  /**
   * Handle filter applied event
   */
  public onFilterApplied(colDef: ColDef<T>, selectedValues: any[]): void {
    if (!colDef.field) return;

    const fieldKey = colDef.field as string;

    

    // Update filter state
    if (selectedValues.length === 0) {
      // Remove filter
      delete this.filterState[fieldKey];
    } else {
      // Set filter
      this.filterState[fieldKey] = selectedValues;
    }


    // Clear filter options cache when filters change
    // Cache keys include filterState, so they need to be recalculated
    this.filterOptionsCache.clear();

    // Apply filters to data
    this.applyFilters();

    // Emit filter changed event (for API mode)
    if (this.mergedConfig.columnFilters?.mode === 'api') {
      this.filterChanged.emit({
        filterState: { ...this.filterState },
        isInitial: false,
      });
    }

    this.cdr.markForCheck();
  }

  /**
   * Handle filter cleared event
   */
  public onFilterCleared(colDef: ColDef<T>): void {
    if (!colDef.field) return;

    const fieldKey = colDef.field as string;
    delete this.filterState[fieldKey];

    // Clear filter options cache when filters change
    this.filterOptionsCache.clear();

    // Apply filters to data
    this.applyFilters();

    // Emit filter changed event (for API mode)
    if (this.mergedConfig.columnFilters?.mode === 'api') {
      this.filterChanged.emit({
        filterState: { ...this.filterState },
        isInitial: false,
      });
    }

    this.cdr.markForCheck();
  }

  /**
   * Check if ANY feature is in API mode (server-side operations)
   *
   * ⚠️ DEPRECATED: This method is deprecated for filtering/sorting decisions.
   * The grid now supports MIXED modes (e.g., pagination API + filters local).
   *
   * For filtering/sorting, check each mode independently:
   * - this.mergedConfig.columnFilters?.mode !== 'api' (for local filtering)
   * - this.mergedConfig.sorting?.mode !== 'api' (for local sorting)
   * - this.mergedConfig.search?.mode !== 'api' (for local search)
   *
   * This method may still be used for general "is anything server-side" checks.
   *
   * @deprecated Use specific mode checks instead (e.g., this.mergedConfig.columnFilters?.mode !== 'api')
   */
  private isApiMode(): boolean {
    return (
      this.mergedConfig.pagination?.mode === 'api' ||
      this.mergedConfig.sorting?.mode === 'api' ||
      this.mergedConfig.columnFilters?.mode === 'api' ||
      this.mergedConfig.search?.mode === 'api'
    );
  }

  /**
   * Apply filters to data source
   * Supports mixed API/Local modes - evaluates each feature mode independently
   */
  private applyFilters(): void {

    let filteredData = [...this.data];

    // Apply column filters ONLY if mode is local (not API)
    const isColumnFiltersLocal = this.mergedConfig.columnFilters?.mode !== 'api';

    if (isColumnFiltersLocal && Object.keys(this.filterState).length > 0) {

      // Pass columnDefs to support custom filter functions
      filteredData = filterByColumnFilters(filteredData, this.filterState, this.columnDefs);

    }

    // Apply search term ONLY if mode is local (not API)
    const isSearchLocal = this.mergedConfig.search?.mode !== 'api';
    if (isSearchLocal && this.searchTerm) {
      filteredData = filterBySearchTerm(
        filteredData,
        this.searchTerm,
        this.mergedConfig.search?.searchableFields as string[],
        this.mergedConfig.search?.caseSensitive
      );
    }

    // Update data source
    this.dataSource.data = filteredData;

    // Re-apply sorting ONLY if mode is local (not API)
    if (this.mergedConfig.sorting?.mode !== 'api' && this.sortState.length > 0) {
      // Preserve expansion state before re-sorting (prevents auto-expansion bug)
      const expandedRowsBeforeSort = new Set(this.expandedRows);

      this.dataSource.data = this.sortDataMultiColumn(this.dataSource.data);

      // Restore expansion state after re-sorting
      // Clear any unintended expansions and restore only the previously expanded rows
      this.expandedRows.clear();
      expandedRowsBeforeSort.forEach(row => {
        // Verify the row still exists in the sorted data before restoring
        if (this.dataSource.data.includes(row)) {
          this.expandedRows.add(row);
        }
      });
    }

    // Emit new data to all footer BehaviorSubjects for reactive updates
    this.footerDataSubjects.forEach((subject) => {
      subject.next(this.dataSource.data);
    });
  }

  // ============================================================================
  // SEARCH LOGIC
  // ============================================================================

  /**
   * Handle search term changed
   */
  public onSearchChanged(searchTerm: string): void {
    const previousTerm = this.searchTerm;
    this.searchTerm = searchTerm;

    // In API mode, emit event instead of filtering locally
    if (this.mergedConfig.search?.mode === 'api') {
      this.searchChanged.emit({
        searchTerm,
        previousTerm,
      });
    } else {
      // Apply all filters locally (column filters + search)
      this.applyFilters();
    }

    this.cdr.markForCheck();
  }

  /**
   * Handle search cleared
   */
  public onSearchCleared(): void {
    const previousTerm = this.searchTerm;
    this.searchTerm = '';

    // In API mode, emit event
    if (this.mergedConfig.search?.mode === 'api') {
      this.searchChanged.emit({
        searchTerm: '',
        previousTerm,
      });
    } else {
      // Apply all filters locally (column filters only)
      this.applyFilters();
    }

    this.cdr.markForCheck();
  }

  // ============================================================================
  // SELECTION LOGIC
  // ============================================================================

  /**
   * Check if selection is enabled
   */
  public isSelectionEnabled(): boolean {
    return this.mergedConfig.selection?.enabled === true;
  }

  /**
   * Check if a specific row is selected
   */
  public isRowSelected(row: T): boolean {
    return this.selectedRows.has(row);
  }

  /**
   * Check if a row is selectable based on configuration
   * @param row - The row data to check
   * @returns true if the row should be selectable, false to disable the checkbox
   */
  public isRowSelectable(row: T): boolean {
    // If selection is not enabled, nothing is selectable
    if (!this.mergedConfig.selection?.enabled) {
      return false;
    }

    // Check if there's a custom function to determine selectability
    if (this.mergedConfig.selection?.isRowSelectable) {
      return this.mergedConfig.selection.isRowSelectable(row);
    }

    // Check if there's a field specified for selectability
    if (this.mergedConfig.selection?.selectableField) {
      return (row as any)[this.mergedConfig.selection.selectableField] ?? true;
    }

    // By default, all rows are selectable
    return true;
  }

  /**
   * Check if all visible selectable rows are selected
   */
  public isAllSelected(): boolean {
    const visibleRows = this.dataSource.data;
    if (visibleRows.length === 0) return false;

    // Filter only selectable rows
    const selectableRows = visibleRows.filter(row => this.isRowSelectable(row));
    if (selectableRows.length === 0) return false;

    // Check if all selectable rows are selected
    return selectableRows.every(row => this.selectedRows.has(row));
  }

  /**
   * Check if some but not all selectable rows are selected (indeterminate state)
   */
  public isSomeSelected(): boolean {
    const visibleRows = this.dataSource.data;
    if (visibleRows.length === 0) return false;

    // Filter only selectable rows
    const selectableRows = visibleRows.filter(row => this.isRowSelectable(row));
    if (selectableRows.length === 0) return false;

    // Count how many selectable rows are selected
    const selectedCount = selectableRows.filter(row => this.selectedRows.has(row)).length;

    // Return true if some but not all selectable rows are selected
    return selectedCount > 0 && selectedCount < selectableRows.length;
  }

  /**
   * Toggle row selection
   */
  public toggleRowSelection(row: T, rowIndex: number, event?: MouseEvent): void {
    if (!this.isSelectionEnabled()) return;

    // Check if the row is selectable before toggling
    if (!this.isRowSelectable(row)) return;

    const mode = this.mergedConfig.selection?.mode || 'multiple';

    if (mode === 'single') {
      // Single selection mode - clear others and select this one
      this.selectedRows.clear();
      this.selectedRows.add(row);
      this.lastClickedRowIndex = rowIndex;
    } else {
      // Multiple selection mode
      if (event?.shiftKey && this.lastClickedRowIndex !== null) {
        // Shift+Click: Range selection
        this.selectRange(this.lastClickedRowIndex, rowIndex);
      } else {
        // Regular click: Toggle selection
        if (this.selectedRows.has(row)) {
          this.selectedRows.delete(row);
        } else {
          this.selectedRows.add(row);
        }
        this.lastClickedRowIndex = rowIndex;
      }
    }

    this.emitSelectionChanged();
    this.cdr.markForCheck();
  }

  /**
   * Select range of rows (for Shift+Click)
   */
  private selectRange(startIndex: number, endIndex: number): void {
    const visibleRows = this.dataSource.data;
    const minIndex = Math.min(startIndex, endIndex);
    const maxIndex = Math.max(startIndex, endIndex);

    for (let i = minIndex; i <= maxIndex; i++) {
      if (i >= 0 && i < visibleRows.length) {
        const row = visibleRows[i];
        // Only select if the row is selectable
        if (this.isRowSelectable(row)) {
          this.selectedRows.add(row);
        }
      }
    }
  }

  /**
   * Toggle all rows selection
   */
  public toggleSelectAll(): void {
    if (!this.isSelectionEnabled()) return;

    const visibleRows = this.dataSource.data;

    if (this.isAllSelected()) {
      // Deselect all visible rows
      visibleRows.forEach(row => this.selectedRows.delete(row));
    } else {
      // Select only selectable visible rows
      visibleRows.forEach(row => {
        if (this.isRowSelectable(row)) {
          this.selectedRows.add(row);
        }
      });
    }

    this.emitSelectionChanged();
    this.cdr.markForCheck();
  }

  /**
   * Select all rows (public API method)
   */
  public selectAll(): void {
    if (!this.isSelectionEnabled()) return;

    const visibleRows = this.dataSource.data;
    visibleRows.forEach(row => this.selectedRows.add(row));

    this.emitSelectionChanged();
    this.cdr.markForCheck();
  }

  /**
   * Deselect all rows (public API method)
   */
  public deselectAll(): void {
    this.selectedRows.clear();
    this.lastClickedRowIndex = null;

    this.emitSelectionChanged();
    this.cdr.markForCheck();
  }

  /**
   * Get selected rows (public API method)
   */
  public getSelectedRows(): T[] {
    return Array.from(this.selectedRows);
  }

  /**
   * Emit selection changed event
   */
  private emitSelectionChanged(): void {
    const selectedRows = this.getSelectedRows();
    this.selectionChanged.emit({
      selectedRows: selectedRows,
      previousSelection: [], // TODO: Track previous selection if needed
      addedRows: [], // TODO: Track added rows if needed
      removedRows: [], // TODO: Track removed rows if needed
      source: 'user',
    });
  }

  /**
   * Handle row click
   */
  public onRowClick(row: T, rowIndex: number, event: MouseEvent): void {
    // Emit row clicked event
    this.rowClicked.emit({ data: row, rowIndex, event });

    // Handle selection if selectOnRowClick is enabled
    if (this.mergedConfig.selection?.selectOnRowClick) {
      this.toggleRowSelection(row, rowIndex, event);
    }

    // Handle row expansion if expandOnClick is enabled
    if (this.mergedConfig.rowExpansion?.expandOnClick) {
      this.toggleRowExpansion(row);
    }
  }

  // ============================================================================
  // CONTEXT MENU LOGIC
  // ============================================================================

  /**
   * Handle right-click on row (context menu)
   * Only opens menu if contextMenu is explicitly enabled in config
   */
  public onRowContextMenu(row: T, rowIndex: number, event: MouseEvent): void {
    // ⚠️ CRITICAL: Only proceed if context menu is explicitly enabled (OPT-IN)
    if (!this.mergedConfig.contextMenu?.enabled) {
      // Context menu is disabled - allow native browser context menu
      return;
    }

    // Context menu is enabled - prevent default and show custom menu
    if (this.mergedConfig.contextMenu.preventDefault !== false) {
      event.preventDefault();
    }

    if (this.mergedConfig.contextMenu.stopPropagation !== false) {
      event.stopPropagation();
    }

    // Check if there are menu items to display
    const items = this.mergedConfig.contextMenu.items || [];
    if (items.length === 0) {
      return;
    }

    // Open context menu via service
    this.contextMenuService.open({
      items: items,
      data: row,
      rowIndex: rowIndex,
      position: {
        x: event.clientX,
        y: event.clientY,
      },
    });

    // Emit context menu opened event
    this.contextMenuOpened.emit({
      data: row,
      rowIndex: rowIndex,
      position: {
        x: event.clientX,
        y: event.clientY,
      },
      browserEvent: event,
    });
  }

  /**
   * Handle context menu item selected
   */
  public onContextMenuItemSelected(event: ContextMenuItemSelectedEvent<T>): void {
    this.contextMenuItemSelected.emit(event);
  }

  /**
   * Check if context menu is enabled
   */
  public isContextMenuEnabled(): boolean {
    return this.mergedConfig.contextMenu?.enabled === true;
  }

  // ============================================================================
  // ROW EXPANSION LOGIC
  // ============================================================================

  /**
   * Check if row expansion is enabled
   */
  public isRowExpansionEnabled(): boolean {
    return this.mergedConfig.rowExpansion?.enabled === true;
  }

  /**
   * Check if a specific row is expanded
   */
  public isRowExpanded(row: T): boolean {
    return this.expandedRows.has(row);
  }

  /**
   * Toggle row expansion
   */
  public toggleRowExpansion(row: T): void {
    if (!this.isRowExpansionEnabled()) return;

    const wasExpanded = this.expandedRows.has(row);

    if (wasExpanded) {
      this.expandedRows.delete(row);
    } else {
      // If multipleExpand is false, clear other expansions first
      if (!this.mergedConfig.rowExpansion?.multipleExpand) {
        this.expandedRows.clear();
      }
      this.expandedRows.add(row);
    }

    // Emit expansion changed event
    this.rowExpansionChanged.emit({
      data: row,
      expanded: !wasExpanded,
      expandedRows: Array.from(this.expandedRows),
    });

    this.cdr.markForCheck();
  }

  /**
   * Expand row
   */
  public expandRow(row: T): void {
    if (!this.isRowExpansionEnabled()) return;

    // If multipleExpand is false, clear other expansions first
    if (!this.mergedConfig.rowExpansion?.multipleExpand) {
      this.expandedRows.clear();
    }

    this.expandedRows.add(row);

    // Emit expansion changed event
    this.rowExpansionChanged.emit({
      data: row,
      expanded: true,
      expandedRows: Array.from(this.expandedRows),
    });

    this.cdr.markForCheck();
  }

  /**
   * Collapse row
   */
  public collapseRow(row: T): void {
    this.expandedRows.delete(row);

    // Emit expansion changed event
    this.rowExpansionChanged.emit({
      data: row,
      expanded: false,
      expandedRows: Array.from(this.expandedRows),
    });

    this.cdr.markForCheck();
  }

  // ============================================================================
  // TREE GRID LOGIC
  // ============================================================================

  /**
   * Check if tree mode is enabled
   */
  public isTreeModeEnabled(): boolean {
    return this.isTreeMode;
  }

  /**
   * Get tree node for a row
   */
  public getTreeNode(row: T): TreeNode<T> | undefined {
    if (!this.isTreeMode) return undefined;
    return this.flattenedTreeData.find(node => node.data === row);
  }

  /**
   * Get tree node level for a row
   */
  public getTreeNodeLevel(row: T): number {
    const node = this.getTreeNode(row);
    return node?.level ?? 0;
  }

  /**
   * Check if row has children
   */
  public hasTreeChildren(row: T): boolean {
    const node = this.getTreeNode(row);
    return node?.hasChildren ?? false;
  }

  /**
   * Check if tree node is expanded
   */
  public isTreeNodeExpanded(row: T): boolean {
    const node = this.getTreeNode(row);
    return node?.expanded ?? false;
  }

  /**
   * Toggle tree node expansion
   */
  public toggleTreeNodeExpansion(row: T): void {
    if (!this.isTreeMode) return;

    const node = this.getTreeNode(row);
    if (!node || !node.hasChildren) return;

    toggleNodeExpansion(node, this.flattenedTreeData);
    this.updateTreeDataSource();
    this.cdr.markForCheck();
  }

  /**
   * Expand tree node
   */
  public expandTreeNode(node: T, expandChildren: boolean = false): void {
    if (!this.isTreeMode) return;

    const treeNode = this.getTreeNode(node);
    if (!treeNode || !treeNode.hasChildren) return;

    expandNode(treeNode, this.flattenedTreeData, expandChildren);
    this.updateTreeDataSource();
    this.cdr.markForCheck();
  }

  /**
   * Collapse tree node
   */
  public collapseTreeNode(node: T, collapseChildren: boolean = false): void {
    if (!this.isTreeMode) return;

    const treeNode = this.getTreeNode(node);
    if (!treeNode || !treeNode.hasChildren) return;

    collapseNode(treeNode, this.flattenedTreeData, collapseChildren);
    this.updateTreeDataSource();
    this.cdr.markForCheck();
  }

  /**
   * Expand all tree nodes
   */
  public expandAllTreeNodes(): void {
    if (!this.isTreeMode) return;

    expandAllTree(this.flattenedTreeData);
    this.updateTreeDataSource();
    this.cdr.markForCheck();
  }

  /**
   * Collapse all tree nodes
   */
  public collapseAllTreeNodes(): void {
    if (!this.isTreeMode) return;

    collapseAllTree(this.flattenedTreeData);
    this.updateTreeDataSource();
    this.cdr.markForCheck();
  }

  /**
   * Get tree indent size
   */
  public getTreeIndentSize(): number {
    return this.mergedConfig.treeData?.indentSize || 20;
  }

  /**
   * Get tree statistics
   */
  public getTreeStats(): {
    totalNodes: number;
    visibleNodes: number;
    expandedNodes: number;
    maxDepth: number;
  } | null {
    if (!this.isTreeMode) return null;

    return {
      totalNodes: getTotalNodeCount(this.flattenedTreeData),
      visibleNodes: getVisibleNodeCount(this.flattenedTreeData),
      expandedNodes: getExpandedNodeCount(this.flattenedTreeData),
      maxDepth: getMaxDepth(this.flattenedTreeData),
    };
  }

  // ============================================================================
  // COLUMN VISIBILITY LOGIC
  // ============================================================================

  /**
   * Check if column visibility toggle is enabled
   */
  public isColumnVisibilityEnabled(): boolean {
    return this.mergedConfig.columnVisibility?.enabled === true;
  }

  /**
   * Handle column visibility changes
   */
  public onColumnVisibilityChanged(options: ColumnVisibilityOption[]): void {
    this.columnVisibilityOptions = options;
    this.updateDisplayedColumns();
    this.cdr.markForCheck();
  }

  // ============================================================================
  // SETTINGS PANEL LOGIC (NEW)
  // ============================================================================

  /**
   * Check if settings panel is enabled
   */
  public isSettingsPanelEnabled(): boolean {
    return this.mergedConfig.settingsPanel?.enabled === true;
  }

  /**
   * Toggle settings panel (MatSidenav)
   */
  public toggleSettingsPanel(): void {
    this.isSettingsPanelOpen = !this.isSettingsPanelOpen;
  }

  /**
   * Close settings panel
   */
  public closeSettingsPanel(): void {
    this.isSettingsPanelOpen = false;
  }

  /**
   * Column visibility methods (moved from grid-settings-panel)
   */
  public onColumnToggle(column: ColumnVisibilityOption): void {
    this.onColumnVisibilityChanged(this.columnVisibilityOptions);
  }

  public showAllColumns(): void {
    this.columnVisibilityOptions.forEach(col => {
      if (!col.disabled) col.visible = true;
    });
    this.onColumnVisibilityChanged(this.columnVisibilityOptions);
  }

  public hideAllColumns(): void {
    this.columnVisibilityOptions.forEach(col => {
      if (!col.disabled) col.visible = false;
    });
    this.onColumnVisibilityChanged(this.columnVisibilityOptions);
  }

  public getVisibleColumnsCount(): number {
    return this.columnVisibilityOptions.filter(col => col.visible).length;
  }

  public getTotalColumnsCount(): number {
    return this.columnVisibilityOptions.length;
  }

  /**
   * Handle density changed from settings panel
   */
  public onDensityChange(density: 'compact' | 'comfortable' | 'spacious'): void {
    this.selectedDensity = density;
    this.onDensityChanged(density);
  }

  /**
   * Handle density changed from settings panel
   */
  public onDensityChanged(density: 'compact' | 'comfortable' | 'spacious'): void {
    this.gridDensity = density;
    // Update responsive classes to apply density
    this.updateResponsiveClasses();
    this.cdr.markForCheck();
  }

  /**
   * Handle export requested from settings panel
   */
  public onExport(format: 'csv' | 'excel'): void {
    this.onExportRequested(format);
  }

  /**
   * Handle export requested from settings panel
   */
  public onExportRequested(format: 'csv' | 'excel'): void {
    const filename = 'export-' + new Date().getTime();
    this.api.exportDisplayedData(format, filename);
  }

  /**
   * Reset settings click handler
   */
  public resetSettings(): void {
    this.onResetSettings();
  }

  /**
   * Handle reset settings from settings panel
   */
  public onResetSettings(): void {
    // Reset to initial/default state
    this.gridDensity = this.mergedConfig.settingsPanel?.defaultDensity || 'comfortable';
    this.selectedDensity = this.gridDensity;
    this.updateResponsiveClasses();
    this.cdr.markForCheck();
  }

  // ============================================================================
  // COLUMN SIZING METHODS (Performance Critical)
  // ============================================================================

  /**
   * Size all columns to fit available space
   * Distributes available width proportionally based on current widths
   */
  public sizeColumnsToFit(): void {
    // This is a simplified version - in production you'd measure container width
    // and calculate proportional widths for each column

    // For now, we ensure columns use the width specified in ColDef
    // Real implementation would require accessing DOM to measure container
    this.visibleColumnDefs.forEach(colDef => {
      if (!colDef.width) {
        colDef.width = 150; // Default width if not specified
      }
    });

    this.cdr.markForCheck();
  }

  /**
   * Auto-size specific columns based on content
   * @param columnIds - Optional array of column IDs to resize. If not provided, resizes all
   */
  public autoSizeColumns(columnIds?: string[]): void {
    const columnsToResize = columnIds
      ? this.visibleColumnDefs.filter(col =>
          columnIds.includes(col.colId || col.field as string)
        )
      : this.visibleColumnDefs;

    columnsToResize.forEach(colDef => {
      // Calculate optimal width based on header and content
      // This is a simplified version - real implementation would measure DOM
      const headerLength = (colDef.headerName || '').length;
      const estimatedWidth = Math.max(100, Math.min(headerLength * 10, 400));

      colDef.width = estimatedWidth;
    });

    this.cdr.markForCheck();
  }

  /**
   * Auto-size all columns
   */
  public autoSizeAllColumns(): void {
    this.autoSizeColumns();
  }

  // ============================================================================
  // UTILITY METHODS
  // ============================================================================

  /**
   * Clear all column filters
   */
  public clearAllFilters(): void {
    this.filterState = {} as FilterState<T>;
    this.applyFilters();
    this.cdr.markForCheck();
  }

  /**
   * Clear all sorts
   */
  public clearAllSorts(): void {
    this.sortState = [];
    this.dataSource.data = [...this.data];
    this.cdr.markForCheck();
  }

  // ============================================================================
  // VIRTUAL SCROLLING METHODS
  // ============================================================================

  /**
   * Check if virtual scrolling is enabled
   */
  public isVirtualScrollingEnabled(): boolean {
    return this.mergedConfig.performance?.virtualScrolling === true;
  }

  /**
   * Get virtual scroll item size (row height)
   */
  public getVirtualScrollItemSize(): number {
    return this.mergedConfig.performance?.virtualScrollItemSize || 48;
  }

  /**
   * Get virtual scroll buffer size
   */
  public getVirtualScrollBufferSize(): number {
    return this.mergedConfig.performance?.virtualScrollBufferSize || 10;
  }

  // ============================================================================
  // PRIVATE HELPER METHODS
  // ============================================================================

  /**
   * Humanize field name for auto-generated headers
   */
  private humanize(field: string): string {
    if (!field) return '';

    return field
      .split('.')
      .pop() // Get last part of nested path
      ?.replace(/([A-Z])/g, ' $1') // Add space before capital letters
      .replace(/^./, str => str.toUpperCase()) // Capitalize first letter
      .trim() || '';
  }

  /**
   * Track by function for *ngFor performance
   */
  public trackByColumnId = (index: number, colDef: ColDef<T>): string => {
    return this.getColumnId(colDef, index);
  }

  /**
   * Track by function for rows
   */
  public trackByRowId = (index: number, row: T): any => {
    const rowIdField = this.mergedConfig.rowIdField || '_id';
    return (row as any)[rowIdField] || index;
  }

  // ============================================================================
  // HEADER RENDERER LOGIC
  // ============================================================================

  /**
   * Get header renderer params for a column
   */
  public getHeaderRendererParams(colDef: ColDef<T>, colIndex: number): any {
    return {
      colDef: colDef,
      displayName: this.getHeaderName(colDef),
      columnIndex: colIndex,
      api: this.api,
      params: colDef.headerRendererParams || {},
    };
  }

  /**
   * Check if column has custom header renderer
   */
  public hasHeaderRenderer(colDef: ColDef<T>): boolean {
    return !!colDef.headerRenderer;
  }

  /**
   * Check if column is currently filtered
   */
  public isColumnFiltered(colDef: ColDef<T>): boolean {
    if (!colDef.field) return false;
    const fieldKey = colDef.field as string;
    return !!this.filterState[fieldKey] && this.filterState[fieldKey].length > 0;
  }

  // ============================================================================
  // FOOTER LOGIC
  // ============================================================================

  /**
   * Check if footer is enabled globally
   */
  public isFooterEnabled(): boolean {
    return this.mergedConfig.footer?.enabled === true;
  }

  /**
   * Get footer cell renderer params for a column (REACTIVE VERSION with Observables)
   * Returns params with a data$ Observable that emits when grid data changes.
   * BehaviorSubjects are cached per column to ensure efficient streaming.
   */
  public getFooterCellRendererParams(colDef: ColDef<T>, colIndex: number): any {
    const footerConfig = colDef.footer;
    if (!footerConfig) {
      return null;
    }

    // Create unique cache key for this column
    const cacheKey = colDef.colId || (colDef.field as string) || `footer-col-${colIndex}`;

    // Create or reuse BehaviorSubject for this column
    if (!this.footerDataSubjects.has(cacheKey)) {
      // Create new BehaviorSubject initialized with current data
      const subject = new BehaviorSubject<T[]>(this.dataSource.data || []);
      this.footerDataSubjects.set(cacheKey, subject);
    }

    const dataSubject = this.footerDataSubjects.get(cacheKey)!;

    // Return params with Observable stream
    return {
      colDef: colDef,
      data$: dataSubject.asObservable(), // ✅ Reactive Observable stream
      data: this.dataSource.data,        // Keep for backwards compatibility
      filteredData: this.dataSource.data,
      value: this.calculateFooterValue(colDef),
      columnIndex: colIndex,
      api: this.api,
      params: footerConfig.footerCellRendererParams || {},
    };
  }

  /**
   * Calculate footer value based on aggregate function
   */
  private calculateFooterValue(colDef: ColDef<T>): any {
    const footerConfig = colDef.footer;
    if (!footerConfig) {
      return null;
    }

    // Return static value if provided
    if (footerConfig.staticValue !== undefined) {
      return footerConfig.staticValue;
    }

    // Use custom aggregate function if provided
    if (footerConfig.aggregateFunction === 'custom' && footerConfig.customAggregateFunction) {
      return footerConfig.customAggregateFunction(this.dataSource.data, colDef);
    }

    // Apply built-in aggregate functions
    if (!footerConfig.aggregateFunction || !colDef.field) {
      return null;
    }

    const values = this.dataSource.data
      .map(row => getNestedValue(row, colDef.field as string))
      .filter(val => val !== null && val !== undefined && !isNaN(Number(val)));

    switch (footerConfig.aggregateFunction) {
      case 'sum':
        return values.reduce((acc, val) => acc + Number(val), 0);

      case 'avg':
        if (values.length === 0) return 0;
        return values.reduce((acc, val) => acc + Number(val), 0) / values.length;

      case 'min':
        if (values.length === 0) return null;
        return Math.min(...values.map(v => Number(v)));

      case 'max':
        if (values.length === 0) return null;
        return Math.max(...values.map(v => Number(v)));

      case 'count':
        return this.dataSource.data.length;

      case 'first':
        if (this.dataSource.data.length === 0) return null;
        return getNestedValue(this.dataSource.data[0], colDef.field as string);

      case 'last':
        if (this.dataSource.data.length === 0) return null;
        return getNestedValue(
          this.dataSource.data[this.dataSource.data.length - 1],
          colDef.field as string
        );

      default:
        return null;
    }
  }

  /**
   * Check if column has footer configuration
   */
  public hasFooter(colDef: ColDef<T>): boolean {
    return !!colDef.footer && this.isFooterEnabled();
  }

  /**
   * Get footer cell classes
   */
  public getFooterCellClasses(colDef: ColDef<T>): string {
    if (!colDef.footer) return '';

    const classes: string[] = [];

    if (colDef.footer.footerClass) {
      if (Array.isArray(colDef.footer.footerClass)) {
        classes.push(...colDef.footer.footerClass);
      } else {
        classes.push(colDef.footer.footerClass);
      }
    }

    return classes.join(' ');
  }

  /**
   * Get footer cell styles
   */
  public getFooterCellStyles(colDef: ColDef<T>): { [key: string]: any } {
    if (!colDef.footer || !colDef.footer.footerStyle) {
      return {};
    }

    return colDef.footer.footerStyle;
  }

  /**
   * Get combined footer styles
   * Merges column styles (width, minWidth, maxWidth) with footer-specific styles
   * Used in template to avoid spread operator syntax which is not supported in Angular templates
   */
  public getCombinedFooterStyles(colDef: ColDef<T>): { [key: string]: any } {
    return {
      ...this.getColumnStyles(colDef),
      ...this.getFooterCellStyles(colDef)
    };
  }
}
